import { useMemo, useState } from "react";
import { PublicRatingSerializer } from "@shared/modules/ratings/types/ratings";
import {
  hasMinimumAverageCustomerReviews,
  hasMinimumTotalCustomerReviews,
} from "@shared/modules/ratings/utils/ratings";
import usePublicVenueRatingsService from "@shared/modules/venue/services/usePublicVenueRatingsService";
import { getFlattenedData } from "@shared/utils/data/data-utils";
import usePublicEventRatingsService from "../data/services/usePublicEventRatingsService";
import { PublicEventReviewSerializer } from "../data/types/events";

const meetsReviewsThreshold = (rating?: PublicRatingSerializer): boolean =>
  hasMinimumTotalCustomerReviews(rating) &&
  hasMinimumAverageCustomerReviews(rating);

export interface UseCustomerReviewsDataParams {
  venueId?: number;
  venueRating?: PublicRatingSerializer;
  eventId?: number;
  eventRating?: PublicRatingSerializer;
  enabled?: boolean;
}

export interface UseCustomerReviewsDataResult {
  overallRating: PublicRatingSerializer;
  reviews: PublicEventReviewSerializer[];
  isLoading: boolean;
  hasNoResults: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  activeStarRating: string;
  setActiveStarRating: (rating: string) => void;
  isUsingEventData: boolean;
}

/**
 * Fetches paginated review comments for display.
 *
 * Given pre-resolved rating data, this hook determines which data source to
 * query (event-level or venue-level reviews), fetches the paginated list of
 * individual review comments, and provides star-filter state.
 *
 * Typical usage:
 *   const { isCustomerReviewsEnabled } = useEventRatings({ ... });
 *   const reviewsData = useCustomerReviewsData({
 *     venueId, venueRating, eventId, eventRating,
 *     enabled: isCustomerReviewsEnabled,
 *   });
 */
export function useCustomerReviewsData({
  venueId,
  venueRating,
  eventId,
  eventRating,
  enabled = true,
}: UseCustomerReviewsDataParams): UseCustomerReviewsDataResult | null {
  const [activeStarRating, setActiveStarRating] = useState<string>("");

  const venueMeetsThreshold = meetsReviewsThreshold(venueRating);
  const eventMeetsThreshold = meetsReviewsThreshold(eventRating);
  const shouldFetchReviews =
    enabled && (venueMeetsThreshold || eventMeetsThreshold);

  const isUsingEventData =
    eventMeetsThreshold && Boolean(eventId) && eventRating !== undefined;

  const overallRating = isUsingEventData ? eventRating : venueRating;

  const { usePaginated: useEventPaginated } = usePublicEventRatingsService();
  const eventQuery = useEventPaginated({
    params: {
      page_size: 5,
      event: eventId ?? 0,
      ordering: "-created",
      ...(activeStarRating && { rating_value: activeStarRating }),
    },
    options: {
      enabled: shouldFetchReviews && isUsingEventData && Boolean(eventId),
    },
  });

  const { usePaginated: useVenuePaginated } = usePublicVenueRatingsService(
    venueId ?? 0
  );
  const venueQuery = useVenuePaginated({
    params: {
      page_size: 5,
      ordering: "-created",
      ...(activeStarRating && { rating_value: activeStarRating }),
    },
    options: {
      enabled: shouldFetchReviews && !isUsingEventData && Boolean(venueId),
    },
  });

  const activeQuery = isUsingEventData ? eventQuery : venueQuery;

  const reviews: PublicEventReviewSerializer[] = useMemo(() => {
    if (!activeQuery.data) return [];
    return getFlattenedData(activeQuery.data);
  }, [activeQuery.data]);

  const handleSetActiveStarRating = (rating: string): void => {
    setActiveStarRating((prev) => (prev === rating ? "" : rating));
  };

  if (!shouldFetchReviews || !overallRating) {
    return null;
  }

  return {
    overallRating,
    reviews,
    isLoading: activeQuery.isLoading,
    hasNoResults: activeQuery.hasNoResults ?? false,
    hasNextPage: activeQuery.hasNextPage ?? false,
    isFetchingNextPage: activeQuery.isFetchingNextPage ?? false,
    fetchNextPage: activeQuery.fetchNextPage,
    activeStarRating,
    setActiveStarRating: handleSetActiveStarRating,
    isUsingEventData,
  };
}

export default useCustomerReviewsData;
